// @story HIR-HR20
// WhatsAppMoments — whether each kind of message to candidates goes on
// WhatsApp at this school (server: services/whatsappMoments.service.js,
// GET / PUT /api/school-settings/whatsapp-moments). Off by default: off means
// email only, and a template Meta approves never switches one on (the
// 27 Oct 2026 incident). Each moment shows the WhatsApp templates it would
// use and Meta's status for each, here in Hiring. A recruitment administrator
// switches one on or off with a reason, after a confirm step that says what
// changes; the server refuses anyone else.
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Input, StatusPill, Switch } from '../../ds';
import { Loading, Note } from '../../components/page';

const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' };
const when = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const META_TONE = { approved: 'success', pending: 'warning', rejected: 'danger', paused: 'danger' };

function isAdmin() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.role === 'admin' || (u.permissions || []).includes('recruitment:admin');
  } catch { return false; }
}

export default function WhatsAppMoments() {
  const admin = isAdmin();
  const [st, setSt] = useState({ phase: 'loading' });
  const [asking, setAsking] = useState(null); // { moment, label, on }
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/school-settings/whatsapp-moments')
      .then((r) => setSt({ phase: 'ready', ...r.data.data }))
      .catch(() => setSt({ phase: 'error' }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = () => {
    if (reason.trim().length < 12) { setMsg({ tone: 'warning', text: 'Give a reason of at least 12 characters. It is kept with the change.' }); return; }
    setBusy(true); setMsg(null);
    api.put('/school-settings/whatsapp-moments', { moment: asking.moment, on: asking.on, reason: reason.trim() })
      .then((r) => { setMsg({ tone: 'success', text: `${r.data.data.label}: ${r.data.data.to}.` }); setAsking(null); setReason(''); load(); })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not change it. Try again.' }))
      .finally(() => setBusy(false));
  };

  if (st.phase === 'loading') return <Loading label="Reading the WhatsApp switches" />;
  if (st.phase === 'error') return <Note tone="warning">The WhatsApp switches could not be read just now. Reload the page to try again.</Note>;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, color: 'var(--text-2)' }}>
        Which messages to candidates may also go on WhatsApp. Off means email only. A WhatsApp template that Meta approves never switches one on by itself.
      </p>
      {st.moments.map((m) => (
        <div key={m.moment} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', paddingBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'grid', gap: 4, minWidth: 260, flex: 1 }}>
            <strong style={{ fontSize: 'var(--fs-body-sm)' }}>{m.label}</strong>
            <span style={muted}>{m.what}</span>
            {(m.templates || []).length > 0 && (
              <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {m.templates.map((t) => (
                  <span key={`${t.gate}-${t.template}`} style={{ display: 'inline-flex', gap: 4, alignItems: 'center', ...muted }}>
                    {t.template} <StatusPill tone={META_TONE[t.meta]}>{`Meta: ${t.meta}`}</StatusPill>
                  </span>
                ))}
              </span>
            )}
            {m.templatesNote && <span style={muted}>{m.templatesNote}</span>}
            {m.changedAt && <span style={muted}>Changed by {m.changedBy} on {when(m.changedAt)}</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <StatusPill tone={m.on ? 'success' : undefined}>{m.text}</StatusPill>
            {admin && (
              <Switch checked={m.on} label={m.on ? 'On' : 'Off'}
                onChange={() => { setAsking({ moment: m.moment, label: m.label, on: !m.on }); setReason(''); setMsg(null); }} />
            )}
          </div>
        </div>
      ))}
      {asking && (
        <div style={{ display: 'grid', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
          <strong>{asking.on ? `Switch WhatsApp on for ${asking.label.toLowerCase()}?` : `Back to email only for ${asking.label.toLowerCase()}?`}</strong>
          <span style={muted}>
            {asking.on
              ? 'From the next message at this moment, candidates with a mobile number also get it on WhatsApp, using the approved template. Nothing already sent changes.'
              : 'From the next message at this moment, candidates get it by email only.'}
          </span>
          <Input label="Why, and on whose word" value={reason} onChange={(e) => setReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={save} disabled={busy}>{asking.on ? 'Switch WhatsApp on' : 'Email only'}</Button>
            <Button variant="ghost" onClick={() => setAsking(null)}>Cancel</Button>
          </div>
        </div>
      )}
      {!admin && <Note tone="info">Only a recruitment administrator can switch WhatsApp on or off.</Note>}
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </div>
  );
}
